import { Button, List, ListItem, Notice, Separator, TSeparatorSpacing } from 'telia-front-react';
import { observer } from 'mobx-react';
import { Notice as IUiNotice } from '../CommonInterfaces';
import * as React from 'react';
import GaDataStore from '../stores/GaDataStore';
import useStores from '../stores/UseStores';
import NoticeWithButton from './NoticeWithButton';
import SmsAuthNotice from './SmsAuthNotice';
import UpdateSoftwareNotice from './UpdateSoftwareNotice';

interface UiNoticeProps {
  only?: string[];
  excluding?: string[];
  separatorSpacing?: TSeparatorSpacing;
}

const UiNotice = observer(({ only, excluding, separatorSpacing }: UiNoticeProps): React.JSX.Element | null => {
  const {
    translateStore: { translate, translateHtml },
    uiStore,
    passkeyStore,
  } = useStores();
  if (!uiStore.hasNotice) {
    return null;
  }

  const resolveCustomNotice = (notice: IUiNotice, message?: any) => {
    if ((notice.message || (notice.children as string))?.startsWith('smsauth.buttonError')) {
      return <SmsAuthNotice message={message} />;
    }
    if (notice.children === 'updateSoftwareNotice') {
      return <UpdateSoftwareNotice />;
    }
    if (notice.children === 'passkey.register.error') {
      return (
        <NoticeWithButton
          noticeProps={{ type: 'error' }}
          text={translate('passkey.add.errorNotice')}
          buttonProps={{
            text: translate('notice.tryAgain'),
            size: 'small',
            iconRight: '#arrow-right',
            onClick: passkeyStore.handleAddNewPasskeyOnAccountPage,
          }}
        />
      );
    }
    return null;
  };

  return (
    <>
      {uiStore.notices
        ?.filter((notice) => !!notice)
        .filter((notice) => !only || only.includes(notice.children as string) || (notice.message && only.includes(notice.message)))
        .filter((notice) => !excluding || !excluding.includes(notice.children as string))
        .map((notice, index) => {
          if (notice.jsx) {
            return notice.jsx;
          }
          const defaultMessage = notice.type === 'error' ? 'external.error.general' : 'login.successFallbackMsg';
          const messageType = notice.type;
          const isError = messageType === 'error';
          const message = !!notice.children
            ? translate(`${notice.children}`, notice.paramObject)
            : !!notice.renderedMessage
              ? translateHtml(notice.renderedMessage)
              : translate(defaultMessage);
          const onCloseClick = notice.id ? () => uiStore.hideNotices() : undefined;

          if (isError) {
            GaDataStore.error(typeof message !== 'string' ? message?.props?.dangerouslySetInnerHTML?.__html : message);
          }

          const customNotice = resolveCustomNotice(notice, message);

          return (
            <React.Fragment key={`ui-notice-${notice.id || index}`}>
              {customNotice ? (
                customNotice
              ) : (
                <Notice
                  id={typeof notice.children === 'string' ? notice.children : undefined}
                  type={messageType}
                  onCloseClick={onCloseClick}
                  attributes={{ 'aria-live': 'polite', 'aria-atomic': 'true' }}
                >
                  <div>
                    {message}
                    {notice.erroredFieldIds && notice.erroredFieldIds.length > 0 && (
                      <>
                        <Separator spacing="xxsmall" color="invisible" />
                        <List type="unstyled">
                          {notice.erroredFieldIds.map((erroredField) => (
                            <ListItem key={`erroredField-${erroredField}-button`}>
                              <Button
                                text={translate(`errored.field.button.${erroredField}`)}
                                type="withdrawal"
                                link
                                onClick={() => {
                                  document.querySelectorAll(`#${erroredField}`).forEach((element) => {
                                    // @ts-ignore
                                    element.offsetParent !== null && element.focus();
                                  });
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </>
                    )}
                  </div>
                </Notice>
              )}
              <Separator color={'invisible'} spacing={separatorSpacing || 'xsmall'} />
            </React.Fragment>
          );
        })}
    </>
  );
});

export default UiNotice;
